import { GAP_TYPE_META } from '../gap-meta';
import { useAppConfig } from '../state/config';
import type { Gap } from '../types';

import { GapTypeBadge } from './GapTypeBadge';
import { Modal } from './Modal';

/**
 * Read-only quick-view for a gap, launched by clicking the card. Shows the
 * full description (scrollable when long) and exposes Edit / Delete to jump
 * into the shared composer or the delete-confirm dialog.
 */
export function GapQuickViewDialog({
  gap,
  onClose,
  onEdit,
  onDelete,
}: {
  gap: Gap;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const cfg = useAppConfig();
  const geo = cfg.geographies.find((g) => g.id === gap.geography);
  const headline =
    gap.gap_type === 'new_form_required' && gap.proposed_form_name
      ? gap.proposed_form_name
      : gap.form_name_snapshot || GAP_TYPE_META[gap.gap_type].label;

  return (
    <Modal title="Gap details" size="xl" onClose={onClose}>
      <div className="flex flex-wrap items-center gap-2">
        <GapTypeBadge type={gap.gap_type} />
        <span
          className="chip bg-slate-100 text-slate-700 font-mono font-semibold"
          title={geo?.name ?? gap.geography}
        >
          {geo?.code ?? gap.geography}
          {geo?.name ? (
            <span className="ml-1 font-sans font-normal text-slate-500">
              {geo.name}
            </span>
          ) : null}
        </span>
        {gap.kb_number && (
          <span className="text-xs font-mono text-slate-400">
            {gap.kb_number}
          </span>
        )}
      </div>

      {headline && (
        <h3 className="mt-3 text-base font-semibold text-slate-900 break-words">
          {headline}
        </h3>
      )}
      {gap.scope === 'form' && gap.form_name_snapshot && headline !== gap.form_name_snapshot && (
        <div className="text-xs text-slate-500 break-words">
          {gap.form_name_snapshot}
        </div>
      )}

      <div className="mt-4 max-h-[50vh] overflow-y-auto rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
          {gap.description}
        </p>
      </div>

      <div className="mt-4 flex items-end justify-between gap-2 text-xs text-slate-500">
        <div className="leading-tight min-w-0">
          <div className="truncate" title={gap.author_name}>
            {gap.author_name}
          </div>
          <div className="text-slate-400">{formatShortDate(gap.updated_at)}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button className="btn-secondary h-9" onClick={onEdit}>
            Edit
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md bg-rose-600 text-white text-sm font-medium px-3.5 py-2 shadow-sm hover:bg-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 transition-colors h-9"
            onClick={onDelete}
          >
            Delete
          </button>
        </div>
      </div>
    </Modal>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
